import { Injectable } from '@angular/core';
import { log } from 'mathjs';

@Injectable({
  providedIn: 'root'
})
export class DeflectionCalcService {

  constructor() { 

    // console.log('Service initialized'); // This will only log if the service is instantiated.

  }
  
  calculateDeflection(k: number, ma1: number, theta:number): Map<string, number> {
    const obliqueShockMap = new Map<string, number>();
    const g = Number(k);
    const m = Number(ma1);
    const gm1 = g - 1;
    const gp12 = Math.pow((g + 1), 2);
    const gp1over2 = (g + 1) / 2;
    const gm1over2 = gm1 / 2;
    const m2 = Math.pow(m, 2);
    const t = Number(theta);
    const t_radians = t * (Math.PI / 180);
    const t_2_radians = 2 * t * (Math.PI / 180);
    const sinetheta = Math.sin(t_radians);
    console.log(sinetheta)
    const sin2t = Math.pow(sinetheta, 2);
    const cotantheta = 1 / Math.tan(t_radians);
    const m_sinetheta_2 = Math.pow(m * sinetheta, 2);

    const pre = 2 * cotantheta * (((m2 * sin2t) - 1) / (m2 * (g + Math.cos(t_2_radians)) + 2));
    const delta_radians = Math.atan(pre);
    const delta_degrees = delta_radians * (180 / Math.PI);

    const M2part1 = 1 / Math.sin(t_radians - delta_radians);
    const M2part2top = Math.pow(m * Math.sin(t_radians), 2) + (2 / (g - 1));
    const M2part2bottom = ((2 * g) / (g - 1)) * Math.pow(m * Math.sin(t_radians), 2) - 1;
    const M2 = M2part1 * Math.sqrt(M2part2top / M2part2bottom);

    const p2p1 = ((2 * g) / (g + 1)) * Math.pow(m * sinetheta, 2) - (g - 1) / (g + 1);

    const T2T1 = 1 + (2 * gm1 * ((m2 * sin2t) - 1) * ((g * m2 * sin2t) + 1) / (gp12 * m2 * sin2t));

    const pt2pt1_l_top = gp1over2 * m_sinetheta_2;
    const pt2pt1_l_bottom = 1 + (gm1over2 * m_sinetheta_2);
    const pt2pt1_left = Math.pow((pt2pt1_l_top / pt2pt1_l_bottom), (g / gm1));
    const pt2pt1_right = Math.pow(((2 * g / (g + 1)) * (m_sinetheta_2)) - (gm1 / (g + 1)), (1 / (1 - g)));
    const pt2pt1 = pt2pt1_left * pt2pt1_right;

    const theta_m_delta = t - delta_degrees;
    const theta_m_delta_radians = theta_m_delta * (Math.PI / 180);
    const rho2_rho1 = Math.tan(t_radians) / Math.tan(theta_m_delta_radians);

    obliqueShockMap.set("delta_degrees", delta_degrees);
    obliqueShockMap.set("M2", M2);
    obliqueShockMap.set("p2p1", p2p1);
    obliqueShockMap.set("T2T1", T2T1);
    obliqueShockMap.set("pt2pt1", pt2pt1);
    obliqueShockMap.set("rho2rho1", rho2_rho1);

    return obliqueShockMap;


  }


  getObliqueShock(k: number, ma1: number, delta: number, weak: boolean): Map<string, number> {
    console.log("getObliqueShock callsed!");
    const obliqueShockMap = new Map<string, number>();
    const g = Number(k); // Specific heat ratio
    const m = Number(ma1); // Mach number
    const d = Number(delta); // Deflection angle in degrees
    const m2 = Math.pow(m, 2);
    const m4 = Math.pow(m, 4);
    const gp12 = Math.pow((g + 1), 2);
    const gp1over2 = (g + 1) / 2;
    const gm1 = g - 1;
    const gm1over2 = gm1 / 2;

    const delta_radians = d * (Math.PI / 180);
    const sinedelta = Math.sin(delta_radians);
    const cosinedelta = Math.cos(delta_radians);
    const sin2d = Math.pow(sinedelta, 2);
    const cos2d = Math.pow(cosinedelta, 2);

    const coeff_b = -1 * (((m2 + 2) / m2) + (g * sin2d));
    const coeff_c = ((2 * m2 + 1) / m4) + ((gp12 / 4) + (gm1 / m2)) * sin2d;
    const coeff_d = -1 * (cos2d / m4);

    const Q_param = (3 * coeff_c - Math.pow(coeff_b, 2)) / 9;
    const R_param = ((9 * coeff_b * coeff_c) - (27 * coeff_d) - (2 * (Math.pow(coeff_b, 3)))) / 54;
    const Q3 = Math.pow(Q_param, 3);
    const R2 = Math.pow(R_param, 2);
    const D_param = Q3 + R2;

    let greek_D: number;

    if (R_param >= 0) {
        greek_D = 0;
    } else {
        greek_D = Math.PI;
    }

    const phi = (1 / 3) * (Math.atan(Math.sqrt(-1 * D_param) / R_param) + greek_D);

    let chi: number | null = null;

    if (weak) {
        chi = -1 * (coeff_b / 3) - Math.sqrt(-1 * Q_param) * (Math.cos(phi) - (Math.sqrt(3) * Math.sin(phi)));
    } else {
        chi = -1 * (coeff_b / 3) + 2 * Math.sqrt(-1 * Q_param) * Math.cos(phi);
    }

    const theta_weak = Math.atan(Math.sqrt(chi / (1 - chi)));
    const theta_w_degrees = theta_weak * (180 / Math.PI);

    // Downstream Mach Number (M2)
    const M2part1 = 1 / Math.sin(theta_weak - delta_radians);
    const M2part2top = Math.pow(m * Math.sin(theta_weak), 2) + (2 / (g - 1));
    const M2part2bottom = ((2 * g) / (g - 1)) * Math.pow(m * Math.sin(theta_weak), 2) - 1;
    const M2 = M2part1 * Math.sqrt(M2part2top / M2part2bottom);

    // Static pressure ratio (P2/P1)

    const Mn1 = m * Math.sin(theta_weak); // Normal Mach number
    const p2p1 = 1 + (2 * g / (g + 1)) * (Mn1 ** 2 - 1);


    // Temperature ratio (T2/T1)
    const sinetheta = Math.sin(theta_weak);
    const sinetheta2 = Math.pow(sinetheta, 2);
    const T2T1 = 1 + (2 * gm1 * ((m2 * sinetheta2) - 1) * ((g * m2 * sinetheta2) + 1) / (gp12 * m2 * sinetheta2));

    // Total pressure ratio (pt2/pt1)
    const m_sinetheta_2 = Math.pow(m * sinetheta, 2);
    const pt2pt1_l_top = gp1over2 * m_sinetheta_2;
    const pt2pt1_l_bottom = 1 + (gm1over2 * m_sinetheta_2);
    const pt2pt1_left = Math.pow((pt2pt1_l_top / pt2pt1_l_bottom), (g / gm1));
    const pt2pt1_right = Math.pow((((2 * g) / (g + 1)) * m_sinetheta_2) - (gm1 / (g + 1)), (1 / (1 - g)));
    const pt2pt1 = pt2pt1_left * pt2pt1_right;

    // Density ratio (rho2/rho1)
    const theta_m_delta = theta_w_degrees - d;
    const theta_m_delta_radians = theta_m_delta * (Math.PI / 180);
    const rho2_rho1 = Math.tan(theta_weak) / Math.tan(theta_m_delta_radians);


    // Add results to the map
    obliqueShockMap.set("theta", theta_w_degrees);
    obliqueShockMap.set("M2", M2);
    obliqueShockMap.set("p2p1", p2p1);
    obliqueShockMap.set("T2T1", T2T1);
    obliqueShockMap.set("pt2pt1", pt2pt1);
    obliqueShockMap.set("rho2rho1", rho2_rho1);

    return obliqueShockMap;
  }


}
